<template>
  <div>

    <b-modal v-model="actionModal"
             lazy
             size="xl"
             :title="actionTypeTitle"
             @ok="performActionOnCurrentSelection"
             :ok-disabled="modalActionDisabled">

      <template #modal-footer="{ ok, cancel, hide }">

        <b-button size="sm"
                  :variant="editingSelectionSelectAllButtonVariant('all')"
                  @click="editingSelectionSelectAll(true)">
          Select All
        </b-button>

        <b-button size="sm"
                  :variant="editingSelectionSelectAllButtonVariant('none')"
                  @click="editingSelectionSelectAll(false)">
          Unselect All
        </b-button>

        <div class="flex-grow-1"></div>

        <b-button size="sm" variant="secondary" @click="cancel()">
          Cancel
        </b-button>

        <b-button size="sm" variant="success" @click="ok()">
          {{ actionTypeButtonTitle }}
        </b-button>

      </template>

      <div class="mb-2">
        <b-input v-model="newKeyword" v-if="actionType === 'add'" placeholder="Enter New Keyword"/>
        <div v-if="commaSeparated(currentKeyword)" class="font-italic font-size-sm">Adding commas <code>,</code> will create separate keywords</div>
        <div class="text-danger" v-if="keywordAssetCount(newKeyword) > 0">{{ newKeyword }} already exists on
          <code>{{ keywordAssetCount(newKeyword) }}</code> / <code>{{ selectedAssetIds.length }}</code> assets
        </div>
      </div>

      <h6>
        <span v-if="actionType === 'delete'">
          Select the assets to delete the keyword <code>{{ editing }}</code> from
        </span>
        <span v-if="actionType==='rename'">
          Select the assets to rename the keyword <code>{{ editing }}</code> to <code>{{ editingValue }}</code>
        </span>
        <span v-if="actionType==='add'">
          Select the assets to add the new keyword to
        </span>
        <pulse-loader :loading="bulkActionRunning"
                      v-if="bulkActionRunning"
                      size="10px"
                      color="#00C0F4"
                      class="mx-2"/>
      </h6>

      <div class="d-flex flex-wrap align-items-center">
        <div class="border rounded mr-2 mb-1 p-1" v-for="asset in editingAssets" :class="{'border-success': assetHasKeyword(asset, currentKeyword) }">
          <b-checkbox v-model="editingSelection[asset[idKey]]">
            <div class="img-bg img-bg-1 img-square" :style="`background-image: url('${asset.thumb_url}')`"></div>
          </b-checkbox>
        </div>
      </div>
    </b-modal>

    <div class="d-flex justify-content-between mb-2">
      <b-checkbox v-model="showAssetNumber">Show Asset Numbers</b-checkbox>
      <b-button variant="primary" size="sm" @click="prepareGlobalAdd()">
        New Keyword
      </b-button>
    </div>

    <b-input-group class="mb-2">
      <b-input v-model="query" name="Search" placeholder="Search"/>
      <b-input-group-append v-if="query.length > 0">
        <b-button @click="query = ''"><i class="fas fa-times"></i></b-button>
      </b-input-group-append>
    </b-input-group>

    <div v-if="commaSeparated(query)" class="font-italic font-size-sm">Adding commas <code>,</code> will create separate keywords</div>

    <div v-if="showNotFoundAction" class="mb-2">

      <span v-if="keywordAssetCount(query) > 0">
        <code>{{ query }}</code> is present on <code>{{ keywordAssetCount(query) }}</code> / <code>{{ selectedAssetIds.length }}</code> assets
      </span>
      <span v-else>
        No exact match, add <code>{{ query }}</code> ?
      </span>

      <b-button variant="primary" size="sm" @click="newKeywordOnAll(query)" :disabled="!canAddKeyword(query)">Add to
        All
      </b-button>
      <b-button variant="success" size="sm" @click="prepareGlobalAdd(query)" :disabled="!canAddKeyword(query)">Choose
      </b-button>
    </div>

    <div class="d-flex flex-wrap align-items-center">
      <div class="cursor-pointer border rounded mr-2 mb-1 p-1" v-for="obj in filteredArrayKeywords">
        <div @click="startEditing(obj.keyword)" v-if="editing !== obj.keyword">
          {{ obj.keyword }}
          <b-badge v-if="showAssetNumber" variant="primary" title="assets">
            {{ obj.assets.length }}
          </b-badge>
        </div>

        <div v-if="editing === obj.keyword">
          <div class="d-flex align-items-center mb-2" style="gap: 5px">
            <b-input v-model="editingValue"/>
            <b-badge v-if="showAssetNumber" variant="primary" title="assets">
              {{ obj.assets.length }}
            </b-badge>
          </div>

          <div class="d-flex" style="gap: 5px">
            <b-button variant="success"
                      size="sm"
                      @click="prepareActionType('rename')"
                      v-b-tooltip
                      title="Rename"
                      :disabled="editing === editingValue">
              <i class="fas fa-edit"></i>
            </b-button>
            <b-button variant="danger"
                      size="sm"
                      @click="prepareActionType('delete')"
                      v-b-tooltip.left
                      title="Delete">
              <i class="fas fa-trash"></i>
            </b-button>
            <b-button variant="info"
                      size="sm"
                      @click="prepareActionType('add')"
                      v-b-tooltip.right
                      :title="`Add only to these ${obj.assets.length} assets`">
              <i class="fas fa-plus"></i>
            </b-button>
            <div class="flex-grow-1"></div>
            <b-button variant="secondary" size="sm" @click="stopEditing()" v-b-tooltip title="Close">
              <i class="fas fa-times"></i>
            </b-button>
          </div>

        </div>

      </div>
    </div>

  </div>

</template>

<script>

import GeneralMixins from "../../../../mixins/GeneralMixins";
import PulseLoader from 'vue-spinner/src/PulseLoader';
import {isEqual} from "lodash";

export default {
  props: ['selectedAssets', 'idKey'],

  mixins: [GeneralMixins],

  components: {PulseLoader},

  data() {
    return {
      query: "",
      editing: null,
      editingValue: null,
      editingSelection: {},
      showAssetNumber: true,
      bulkActionRunning: false,
      selectedOption: null,
      actionModal: false,
      actionType: null,
      newKeyword: null
    }
  },

  watch: {
    query(old, val) {
      this.stopEditing()
    }
  },

  methods: {

    editingSelectionSelectAllButtonVariant(type) {
      if (type === 'all') {
        if (this.editingSelectionAllSelected) {
          return "primary"
        } else {
          return "outline-secondary"
        }
      } else {
        if (this.editingSelectionNoneSelected) {
          return "primary"
        } else {
          return "outline-secondary"
        }
      }
    },
    editingSelectionSelectAll(value) {
      for (const key in this.editingSelection) {
        this.editingSelection[key] = value
      }
    },

    newKeywordOnAll(keyword) {
      this.newKeywordOnSelection(keyword, this.selectedAssetIds)
    },

    newKeywordOnSelection(keyword, selectionIds) {
      const attributes = {
        asset_ids: selectionIds,
        keyword: keyword,
      }

      this.bulkActionRunning = true

      this.$http.post("/curators/bulk/asset_keywords/add_keywords", attributes).then(result => {

        let assets = result.body.assets
        let count = assets.length
        this.showToast(`The keyword "${keyword}" was added to ${count} assets`, "Success")

        this.selectedAssets.forEach(asset => {
          let resultAsset = assets.filter(img => img.id === asset[this.idKey])[0]
          if (resultAsset) {
            asset.keywords = resultAsset.keywords
          }
        })

        this.$emit('change', this.selectedAssets)
        this.actionModal = false
        this.newKeyword = null
        this.stopEditing()
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    },

    newKeywordOnCurrentSelection() {
      this.newKeywordOnSelection(this.newKeyword, this.editingSelectionIds)
    },

    renameCurrentSelection() {
      const attributes = {
        asset_ids: this.editingSelectionIds,
        keyword: this.editing,
        rename_to: this.editingValue
      }

      this.bulkActionRunning = true

      this.$http.post("/curators/bulk/asset_keywords/rename_keywords", attributes).then(result => {

        let assets = result.body.assets
        let count = assets.length
        this.showToast(`The keyword "${this.editing}" was renamed to ${this.editingValue} on ${count} assets`, "Success")

        this.selectedAssets.forEach(asset => {
          let resultAsset = assets.filter(img => img.id === asset[this.idKey])[0]
          if (resultAsset) {
            asset.keywords = resultAsset.keywords
          }
        })

        this.$emit('change', this.selectedAssets)
        this.actionModal = false
        // if we haven't renamed ALL of them, set the field value to it's original name
        if (this.editingSelectionIds.length !== Object.keys(this.editingSelection).length) {
          this.editingValue = this.editing
        }
        this.stopEditing()
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    },

    performActionOnCurrentSelection(evt) {
      evt.preventDefault()

      if (this.actionType === "delete") {
        this.deleteCurrentSelection()
      } else if (this.actionType === "rename") {
        this.renameCurrentSelection()
      } else if (this.actionType === "add") {
        this.newKeywordOnCurrentSelection()
      }
    },

    deleteCurrentSelection() {
      const attributes = {
        asset_ids: this.editingSelectionIds,
        keyword: this.editing
      }

      this.bulkActionRunning = true

      this.$http.post("/curators/bulk/asset_keywords/delete_keywords", attributes).then(result => {

        let assets = result.body.assets
        let count = assets.length
        this.showToast(`The keyword "${this.editing}" was deleted from ${count} assets`, "Success")

        this.selectedAssets.forEach(asset => {
          let resultAsset = assets.filter(img => img.id === asset[this.idKey])[0]
          if (resultAsset) {
            asset.keywords = resultAsset.keywords
          }
        })

        this.$emit('change', this.selectedAssets)
        this.actionModal = false
        this.stopEditing()
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    },

    startEditing(keyword) {
      this.editing = keyword
      this.editingValue = keyword
    },

    stopEditing() {
      this.editing = null
      this.editingValue = null
      this.actionType = null
      this.newKeyword = null
    },

    prepareGlobalAdd(keyword) {
      if (keyword) {
        this.newKeyword = keyword
      }

      this.actionType = 'add'
      this.editingSelection = Object.assign({}, ...this.selectedAssetIds.map(id => {
        return {
          [id]: true
        }
      }))
      this.actionModal = true
    },

    prepareActionType(actionType) {
      this.actionType = actionType
      this.editingSelection = Object.assign({}, ...this.groupedKeywords[this.editing].map(asset => {
        return {
          [asset[this.idKey]]: true
        }
      }))
      this.actionModal = true
    },

    canAddKeyword(keyword) {
      if (!this.groupedKeywords[keyword]) {
        return true
      }

      return this.selectedAssetIds.length > this.keywordAssetCount(keyword)
    },

    keywordAssetCount(keyword) {
      if (this.groupedKeywords[keyword]) {
        return this.groupedKeywords[keyword].length
      }

      return 0
    },

    assetHasKeyword(asset, keyword) {
      return asset.keywords.includes(keyword)
    },
    commaSeparated(keyword) {
      return keyword && keyword.includes(',')
    },
  },

  computed: {


    showNotFoundAction() {
      return this.query && this.query.length > 0 && this.canAddKeyword(this.query)
    },

    modalActionDisabled() {
      if (this.actionType === 'add') {
        return this.editingSelectionIds.length === 0 || !this.newKeyword
      } else {
        return this.editingSelectionIds.length === 0
      }
    },

    actionTypeButtonTitle() {
      if (this.actionType === "delete") {
        return `Delete`
      } else if (this.actionType === "rename") {
        return `Rename`
      } else if (this.actionType === 'add') {
        return `Add`
      }
    },

    actionTypeTitle() {
      if (this.actionType === "delete") {
        return `Delete Keyword: ${this.editing}`
      } else if (this.actionType === "rename") {
        return `Rename Keyword: ${this.editing} to ${this.editingValue}`
      } else if (this.actionType === "add") {
        return `Add New Keyword`
      } else {
        return "Invalid Action"
      }
    },

    editingSelectionIds() {
      if (!this.editingSelection) {
        return []
      }

      return Object.keys(this.editingSelection).filter(id => this.editingSelection[id])
    },

    editingSelectionAllSelected() {
      if (!this.editingSelection) {
        return false
      }

      return isEqual(this.editingSelectionIds, Object.keys(this.editingSelection))
    },

    editingSelectionNoneSelected() {
      if (!this.editingSelection) {
        return false
      }

      return isEqual(this.editingSelectionIds, [])
    },

    allUniqueKeywords() {
      const kwArrays = this.selectedAssets.map(el => el.keywords).filter(k => k)
      const kwList = [].concat.apply([], kwArrays)
      return [...new Set(kwList)]
    },

    groupedKeywords() {
      const keywords = this.allUniqueKeywords
      return keywords.reduce((obj, keyword) => {
        obj[keyword] = this.selectedAssets.filter(el => el.keywords.includes(keyword))
        return obj
      }, {})
    },

    editingAssets() {
      if (this.editing) {
        return this.groupedKeywords[this.editing]
      } else {
        return this.selectedAssets
      }
    },

    groupedArrayKeywords() {
      return Object.entries(this.groupedKeywords).map(el => {
        return {keyword: el[0], assets: el[1]}
      }).sort((a, b) => a.keyword.localeCompare(b.keyword))
    },

    filteredArrayKeywords() {
      if (this.query && this.query.length > 0) {
        return this.groupedArrayKeywords.filter(el => el.keyword.indexOf(this.query) !== -1)
      } else {
        return this.groupedArrayKeywords
      }
    },

    selectedAssetIds: function () {
      return this.selectedAssets.map(el => el[this.idKey])
    },

    currentKeyword() {
      if (this.actionType === 'add') {
        return this.newKeyword
      } else {
        return this.editingValue
      }
    }

  },
}
</script>
